import React from "react";
import { Link } from "react-router-dom";

import { useProfile } from "app/context";
import { Trans } from "app/utils";

import * as CHQ from "chq";
import getUserName from "app/utils/getUserName";

export const getProfilePath = (
  user: CHQ.User,
  landingPage: boolean,
  preview: boolean,
  currentUserId?: number
) => {
  if (currentUserId && user.id !== currentUserId) {
    return `/community/${user.id}`;
  }

  if (preview) {
    return `/admin/landing-page/preview/users/${user.id}`;
  }

  if (landingPage) {
    return `/users/${user.id}`;
  }

  return `/community/${user.id}`;
};

type ProfileLinkProps = {
  ariaLabel?: string;
  children?: React.ReactNode;
  commonality?: boolean;
  tabIndex?: number;
  user: null | CHQ.User;
  landingPage?: boolean;
  organization?: string;
  preview?: boolean;
  justFirstName?: boolean;
};

const ProfileLink: React.FC<ProfileLinkProps> = ({
  ariaLabel,
  children,
  commonality,
  user, tabIndex,
  landingPage = false,
  preview = false,
  justFirstName = false,
  ...props
}) => {
  const { profile } = useProfile();

  if (!user) {
    return <Trans>An anonymous user</Trans>;
  }

  const getName = () => getUserName(user, !justFirstName);

  const inner = ariaLabel ? null : children || getName();

  if (!user.active && !commonality) {
    return (
      <Link
        {...props}
        aria-disabled
        aria-label={ariaLabel}
        to={getProfilePath(user, landingPage, preview)}
        tabIndex={tabIndex || 0}
      >
        {inner}
      </Link>
    );
  }

  if (profile.id === user.id) {
    return <Link {...props} aria-label={ariaLabel} to="/profile" tabIndex={tabIndex || 0}>{inner}</Link>;
  }

  return (
    <Link {...props} aria-label={ariaLabel || `${getUserName(user)}'s profile picture`} to={getProfilePath(user, landingPage, preview, profile.id)} tabIndex={tabIndex || 0}>
      {inner}
    </Link>
  );
};

export default ProfileLink;
